import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft, Globe, CheckCircle2, XCircle, Clock, Loader2,
  ExternalLink, Shield, Layers, Server, Link2, RefreshCw, Copy,
  ChevronRight, AlertTriangle, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL, API } from '@/lib/backendUrl';


const LAYER_COLORS = {
  L1: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
  L2: 'bg-purple-500/15 text-purple-400 border-purple-500/20',
};

const STATUS_CONFIG = {
  live: { label: 'Live', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20', icon: CheckCircle2 },
  partial: { label: 'Partial', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20', icon: AlertTriangle },
  not_deployed: { label: 'Not Deployed', color: 'text-gray-500', bg: 'bg-gray-500/10 border-gray-500/20', icon: Clock },
  pending: { label: 'Pending', color: 'text-gray-500', bg: 'bg-gray-500/10 border-gray-500/20', icon: Clock },
};

function ChainCard({ chain, onVerify, verifying }) {
  const status = STATUS_CONFIG[chain.status] || STATUS_CONFIG.pending;
  const StatusIcon = status.icon;

  return (
    <Card className="bg-white/[0.03] border-white/5 overflow-hidden" data-testid={`chain-card-${chain.key}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${chain.is_home_chain ? 'bg-[#F7931A]/15 border border-[#F7931A]/25' : 'bg-white/[0.05] border border-white/10'}`}>
              <Globe className={`w-5 h-5 ${chain.is_home_chain ? 'text-[#F7931A]' : 'text-gray-400'}`} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold heading-font text-gray-100 text-sm">{chain.name}</h3>
                {chain.is_home_chain && (
                  <Badge variant="outline" className="text-[9px] border-[#F7931A]/30 text-[#F7931A] bg-[#F7931A]/5">HOME</Badge>
                )}
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <Badge variant="outline" className={`text-[9px] ${LAYER_COLORS[chain.layer]}`}>{chain.layer}</Badge>
                <span className="text-[10px] text-gray-600 mono">Chain {chain.chain_id}</span>
                <span className="text-[10px] text-gray-600 mono">LZ EID {chain.lz_eid}</span>
              </div>
            </div>
          </div>
          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${status.bg}`}>
            <StatusIcon className={`w-3 h-3 ${status.color}`} />
            <span className={`text-[10px] font-medium ${status.color}`}>{status.label}</span>
          </div>
        </div>

        {/* Contracts */}
        <div className="space-y-2">
          {Object.entries(chain.contracts).map(([name, contract]) => (
            <div key={name} className="flex items-center justify-between py-2 px-3 rounded-lg bg-white/[0.02] border border-white/5">
              <div className="flex items-center gap-2 min-w-0">
                {contract.deployed ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                ) : (
                  <XCircle className="w-3.5 h-3.5 text-gray-600 shrink-0" />
                )}
                <span className="text-xs text-gray-300 truncate">{contract.label}</span>
              </div>
              {contract.deployed ? (
                <button
                  onClick={() => navigator.clipboard.writeText(contract.address).then(() => toast.success('Copied!'))}
                  className="text-[10px] mono text-gray-500 hover:text-gray-300 transition-colors flex items-center gap-1"
                  data-testid={`contract-addr-${name}`}
                >
                  {contract.address.slice(0, 6)}...{contract.address.slice(-4)}
                  <Copy className="w-2.5 h-2.5" />
                </button>
              ) : (
                <span className="text-[10px] text-gray-600 italic">Not configured</span>
              )}
            </div>
          ))}
        </div>

        {/* Verify button */}
        <div className="mt-4 flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onVerify(chain.key)}
            disabled={verifying === chain.key || chain.status === 'not_deployed'}
            className="flex-1 h-8 text-xs border-white/10 text-gray-400 hover:text-gray-200 hover:bg-white/5"
            data-testid={`verify-btn-${chain.key}`}
          >
            {verifying === chain.key ? (
              <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Verifying...</>
            ) : (
              <><Shield className="w-3 h-3 mr-1.5" />Verify Contracts</>
            )}
          </Button>
          <a
            href={chain.explorer}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-white/10 text-gray-500 hover:text-gray-300 hover:bg-white/5 transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </CardContent>
    </Card>
  );
}

function VerificationResult({ result, onClose }) {
  if (!result) return null;

  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-xl p-5 space-y-3" data-testid="verification-result">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {result.rpc_healthy ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          ) : (
            <XCircle className="w-4 h-4 text-red-500" />
          )}
          <h3 className="font-semibold heading-font text-sm text-gray-100">{result.name} Verification</h3>
          <Badge variant="outline" className={`text-[9px] ${result.env === 'testnet' ? 'border-amber-500/30 text-amber-400' : 'border-emerald-500/30 text-emerald-400'}`}>
            {result.env}
          </Badge>
        </div>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-300">
          <XCircle className="w-4 h-4" />
        </button>
      </div>

      <div className="flex items-center gap-4 text-xs text-gray-500">
        <span>RPC: {result.rpc_healthy ? <span className="text-emerald-400">Healthy</span> : <span className="text-red-400">Down</span>}</span>
        {result.block_number > 0 && <span className="mono">Block #{result.block_number.toLocaleString()}</span>}
      </div>

      <div className="space-y-2">
        {Object.entries(result.contracts || {}).map(([name, res]) => (
          <div key={name} className={`rounded-lg px-4 py-3 border ${res.verified ? 'bg-emerald-500/5 border-emerald-500/10' : 'bg-red-500/5 border-red-500/10'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {res.verified ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <XCircle className="w-3.5 h-3.5 text-red-500" />}
                <span className="text-xs font-medium text-gray-200">{name.replace(/_/g, ' ')}</span>
              </div>
              {res.verified && res.data?.address && (
                <span className="text-[10px] mono text-gray-500">{res.data.address.slice(0, 10)}...</span>
              )}
            </div>
            {res.verified && res.data && (
              <div className="flex flex-wrap gap-3 mt-2 text-[10px] text-gray-500">
                {res.data.symbol && <span>Symbol: <span className="text-gray-300">{res.data.symbol}</span></span>}
                {res.data.total_supply !== undefined && <span>Supply: <span className="text-gray-300 mono">{res.data.total_supply.toLocaleString()}</span></span>}
                {res.data.total_minted !== undefined && <span>Minted: <span className="text-gray-300 mono">{res.data.total_minted}</span></span>}
                {res.data.proposal_count !== undefined && <span>Proposals: <span className="text-gray-300 mono">{res.data.proposal_count}</span></span>}
              </div>
            )}
            {!res.verified && res.error && (
              <p className="text-[10px] text-red-400 mt-1">{res.error}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function LzPeersPanel({ peers }) {
  if (!peers || Object.keys(peers).length === 0) return null;

  const peerList = Object.entries(peers);

  return (
    <div className="bg-white/[0.03] border border-white/5 rounded-xl p-5" data-testid="lz-peers-panel">
      <div className="flex items-center gap-2 mb-4">
        <Link2 className="w-4 h-4 text-[#F7931A]" />
        <h3 className="font-semibold heading-font text-sm text-gray-100">LayerZero V2 Peer Map</h3>
      </div>
      <div className="space-y-2">
        {peerList.map(([key, peer]) => (
          <div key={key} className="flex items-center justify-between py-2 px-3 rounded-lg bg-white/[0.02] border border-white/5">
            <div className="flex items-center gap-2">
              <Zap className="w-3 h-3 text-[#F7931A]" />
              <span className="text-xs text-gray-300">{peer.name}</span>
              <Badge variant="outline" className={`text-[9px] ${peer.env === 'testnet' ? 'border-amber-500/20 text-amber-500' : 'border-emerald-500/20 text-emerald-500'}`}>
                {peer.env}
              </Badge>
            </div>
            <div className="flex items-center gap-3 text-[10px] text-gray-500">
              <span className="mono">EID {peer.lz_eid}</span>
              <span className="mono text-gray-400">{peer.oft_address?.slice(0, 8)}...</span>
            </div>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-gray-600 mt-3 text-center">
        OFT tokens on all chains are peered via LayerZero V2 for cross-chain transfers
      </p>
    </div>
  );
}

export default function DeploymentDashboardPage({ embedded = false }) {
  const [chains, setChains] = useState([]);
  const [summary, setSummary] = useState(null);
  const [peers, setPeers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(null);
  const [verifyResult, setVerifyResult] = useState(null);
  const [activeEnv, setActiveEnv] = useState('all');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [chainsRes, summaryRes, peersRes] = await Promise.all([
        fetch(`${API}/api/deployments/chains`),
        fetch(`${API}/api/deployments/summary`),
        fetch(`${API}/api/deployments/lz-peers`),
      ]);
      const [chainsData, summaryData, peersData] = await Promise.all([
        chainsRes.json(), summaryRes.json(), peersRes.json(),
      ]);
      setChains(chainsData.chains || []);
      setSummary(summaryData);
      setPeers(peersData.peers || {});
    } catch (e) {
      console.error('Failed to load deployment data:', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleVerify = async (chainKey) => {
    setVerifying(chainKey);
    setVerifyResult(null);
    try {
      const res = await fetch(`${API}/api/deployments/verify/${chainKey}`);
      const data = await res.json();
      setVerifyResult(data);
      if (data.all_verified) toast.success(`${data.name}: All contracts verified`);
      else toast.warning(`${data.name}: Some contracts could not be verified`);
    } catch (e) {
      toast.error('Verification failed');
    }
    setVerifying(null);
  };

  const filteredChains = chains.filter(c => {
    if (activeEnv === 'all') return true;
    return c.env === activeEnv;
  });

  const testnetChains = chains.filter(c => c.env === 'testnet');
  const mainnetChains = chains.filter(c => c.env === 'mainnet');

  return (
    <div className={embedded ? '' : 'min-h-screen bg-[#0a0e1a]'} data-testid="deployment-dashboard-page">
      {/* Nav */}
      {!embedded && (
      <nav className="sticky top-0 z-50 border-b border-white/5 bg-[#0a0e1a]/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/protocol" className="text-gray-400 hover:text-white transition-colors" data-testid="back-to-protocol">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-[#F7931A] to-[#B08D57] flex items-center justify-center">
                <Server className="w-4 h-4 text-white" />
              </div>
              <span className="heading-font text-base font-semibold tracking-tight">Deployments</span>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={loadData}
            className="border-white/10 text-gray-400 hover:text-gray-200"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh
          </Button>
        </div>
      </nav>
      )}

      <div className={embedded ? 'px-4 lg:px-6 py-6' : 'max-w-6xl mx-auto px-4 py-8'}>
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl heading-font font-bold tracking-tight mb-2" data-testid="deployment-heading">
            Multi-Chain Deployments
          </h1>
          <p className="text-gray-400 text-sm">
            Contract deployment status across testnet and production chains. Verify on-chain contract health.
          </p>
        </div>

        {/* Summary stats */}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="deployment-summary">
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold heading-font mono text-[#F7931A]">{testnetChains.length}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">Testnet Chains</p>
            </div>
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold heading-font mono text-emerald-400">{summary.testnet?.total_deployed || 0}/{summary.testnet?.total_contracts || 0}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">Testnet Contracts</p>
            </div>
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold heading-font mono text-gray-100">{mainnetChains.length}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">Mainnet Chains</p>
            </div>
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold heading-font mono text-gray-400">{summary.mainnet?.total_deployed || 0}/{summary.mainnet?.total_contracts || 0}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">Mainnet Contracts</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeEnv} onValueChange={setActiveEnv} className="mb-6">
          <TabsList className="bg-white/[0.03] border border-white/5" data-testid="env-tabs">
            <TabsTrigger value="all" className="text-xs" data-testid="tab-all">All Chains</TabsTrigger>
            <TabsTrigger value="testnet" className="text-xs" data-testid="tab-testnet">Testnet</TabsTrigger>
            <TabsTrigger value="mainnet" className="text-xs" data-testid="tab-mainnet">Mainnet</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Verification result */}
        {verifyResult && (
          <div className="mb-6">
            <VerificationResult result={verifyResult} onClose={() => setVerifyResult(null)} />
          </div>
        )}

        {/* Chain cards */}
        {loading ? (
          <div className="text-center py-16">
            <Loader2 className="w-8 h-8 text-[#F7931A] mx-auto mb-3 animate-spin" />
            <p className="text-gray-500 text-sm">Loading deployment data...</p>
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="chains-grid">
              {filteredChains.map(chain => (
                <ChainCard
                  key={chain.key}
                  chain={chain}
                  onVerify={handleVerify}
                  verifying={verifying}
                />
              ))}
            </div>

            {/* LZ Peers */}
            <LzPeersPanel peers={peers} />
          </>
        )}
      </div>
    </div>
  );
}
